import { GridColumnVisibilityModel } from "@mui/x-data-grid";
import { useEffect, useRef, useState } from "react";

import { DetectorModes } from "types/createDetector";

export const colsVisibilityDefaultState = {
  created_at: true,
  current_best_answer_str: true,
  current_best_answer_source: true,
  current_best_answer_confidence: true,
  best_answer_feedback_text: false,
  system_answer_str: true,
  system_answer_source: true,
  system_answer_confidence: true,
  first_answer_feedback_text: false,
  ml_answer_str: true,
  ml_answer_confidence: true,
  iq_id: true,
  current_best_prediction_str: true,
  current_best_prediction_source: true,
  current_best_prediction_confidence: true,
  system_prediction_str: true,
  system_prediction_source: true,
  system_prediction_confidence: true,
  ml_prediction_str: true,
  ml_prediction_confidence: true,
  ground_truth_str: true,
  is_audited: true,
};

type ColumnVisibilityModel = typeof colsVisibilityDefaultState;

const VISIBLE_COLUMNS_BY_MODE: Record<DetectorModes, ColumnVisibilityModel> = {
  [DetectorModes.BINARY]: colsVisibilityDefaultState,
  [DetectorModes.COUNT]: colsVisibilityDefaultState,
  [DetectorModes.TEXT_RECOGNITION]: {
    ...colsVisibilityDefaultState,
    current_best_answer_confidence: false,
    system_answer_str: false,
    system_answer_source: false,
    system_answer_confidence: false,
    ml_answer_str: false,
    ml_answer_confidence: false,
    current_best_prediction_str: false,
    current_best_prediction_source: false,
    current_best_prediction_confidence: false,
    system_prediction_str: false,
    system_prediction_source: false,
    system_prediction_confidence: false,
    ml_prediction_str: false,
    ml_prediction_confidence: false,
    ground_truth_str: false,
    is_audited: false,
  },
  [DetectorModes.MULTICHOICE]: colsVisibilityDefaultState,
  [DetectorModes.BOUNDING_BOX]: colsVisibilityDefaultState,
};

const SESSION_STORAGE_KEY = "columnVisibility";

export const useColumnVisibility = ({ mode }: { mode?: DetectorModes }) => {
  // Track the previous mode to detect actual mode changes (switching detectors)
  const prevModeRef = useRef<DetectorModes | undefined>(undefined);

  // Load initial state from sessionStorage or use default
  const loadInitialState = () => {
    const savedState = sessionStorage.getItem(SESSION_STORAGE_KEY);
    // Always use saved state if available, regardless of mode
    return savedState ? JSON.parse(savedState) : colsVisibilityDefaultState;
  };

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(loadInitialState());

  // Update sessionStorage when columnVisibilityModel changes
  useEffect(() => {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(columnVisibilityModel));
  }, [columnVisibilityModel]);

  // Only reset visibility when switching to a DIFFERENT detector mode
  // Not when mode goes undefined -> defined (same detector reloading)
  useEffect(() => {
    if (mode) {
      // Only reset if we had a previous mode AND it's different from current mode
      // This means switching from one detector type to another
      if (prevModeRef.current !== undefined && prevModeRef.current !== mode) {
        setColumnVisibilityModel(VISIBLE_COLUMNS_BY_MODE[mode]);
      }
      prevModeRef.current = mode;
    }
  }, [mode]);

  const handleChangeColumnVisibility = (newValues: ColumnVisibilityModel) => {
    setColumnVisibilityModel({
      ...columnVisibilityModel,
      ...newValues,
    });
  };

  return { columnVisibilityModel, handleChangeColumnVisibility, setColumnVisibilityModel };
};
